import React from 'react';
import { Button } from '../ui/button';
import { ArrowRight, Calendar, CheckCircle2, ShieldCheck, Sparkles } from 'lucide-react';

/**
 * Step 5 — Success screen: shows assigned agent + Calendly CTA + profile
 * summary. Terminal step in the funnel.
 */
export function SuccessStep({ result, form, config, budgetOptions }) {
  return (
    <div className="space-y-5 text-center py-4" data-testid="step-success">
      <div className="w-16 h-16 rounded-full bg-[var(--gp-gold)]/15 flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-8 h-8 text-[var(--gp-gold)]" />
      </div>
      <div>
        <h3 className="font-serif text-2xl md:text-3xl text-[hsl(var(--primary))]">
          Profilo qualificato.
        </h3>
        {result?.budget_threshold_hit ? (
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Il tuo profilo ha attivato il <b className="text-[var(--gp-gold)]">canale diretto con il CEO</b> Richard Demichele.
            Prenota subito la tua call.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Ti abbiamo assegnato il <b>referente dedicato</b> al tuo profilo. Prenota la call quando preferisci.
          </p>
        )}
      </div>

      {/* Assigned agent card with Calendly CTA */}
      {result?.assigned_agent && (
        <div
          className={`rounded-xl p-4 md:p-5 max-w-md mx-auto text-left ${
            result.assigned_agent.is_ceo
              ? 'bg-gradient-to-br from-[var(--gp-gold)]/15 to-[var(--gp-forest)]/8 border-2 border-[var(--gp-gold)]'
              : 'bg-[var(--surface-2)] border border-border'
          }`}
          data-testid="assigned-agent-card"
        >
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
              result.assigned_agent.is_ceo
                ? 'bg-[var(--gp-gold)] text-[var(--gp-forest)]'
                : 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
            }`}>
              {result.assigned_agent.name?.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[hsl(var(--primary))] truncate" data-testid="agent-name">
                {result.assigned_agent.name}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {result.assigned_agent.role === 'ceo' ? 'CEO · HCF Property'
                  : result.assigned_agent.role === 'co_ceo' ? 'Co-CEO · HCF Property'
                    : 'Referente dedicato'}
              </div>
            </div>
          </div>

          {result.assigned_agent.has_calendly ? (
            <a
              href={result.assigned_agent.calendly_url}
              target="_blank"
              rel="noreferrer"
              className="block mt-4"
              data-testid="agent-calendly-cta"
            >
              <Button
                className={`w-full h-11 font-semibold ${
                  result.assigned_agent.is_ceo
                    ? 'bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]'
                    : ''
                }`}
              >
                <Calendar className="w-4 h-4 mr-2" /> Prenota la call ora
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          ) : (
            <div className="mt-4 p-3 rounded-md bg-yellow-50 border border-yellow-200 text-[12px] text-yellow-900">
              <b>Calendly in configurazione.</b> Ti contatteremo direttamente via Telegram entro 1–2 ore lavorative.
            </div>
          )}
        </div>
      )}

      {/* Multi-country follow-up notice */}
      {result?.multi_country_followup && (
        <div
          className="rounded-lg p-3 max-w-md mx-auto bg-[var(--gp-forest)]/8 border border-[var(--gp-forest)]/15 text-[12px] text-left flex items-start gap-2"
          data-testid="multi-country-notice"
        >
          <Sparkles className="w-4 h-4 text-[var(--gp-gold)] flex-shrink-0 mt-0.5" />
          <div>
            <b>Hai indicato più di un paese.</b> Dopo la prima call, il nostro bot Telegram ti scriverà
            per fissare un confronto dedicato con il referente degli altri resort di tuo interesse.
          </div>
        </div>
      )}

      <div className="bg-[var(--surface-2)] rounded-lg p-3 max-w-md mx-auto text-left text-[12px]">
        <div className="flex items-center gap-2 text-muted-foreground text-[10px] uppercase tracking-wider mb-1.5">
          <ShieldCheck className="w-3 h-3" /> Riepilogo profilo
        </div>
        <div className="space-y-0.5">
          <div><b>Paesi:</b> {form.countries.join(', ')}</div>
          <div><b>Percorso:</b> {form.path?.replace('_', ' ')}</div>
          <div><b>Budget:</b> {budgetOptions.find((b) => b.id === form.budget_bracket)?.label}</div>
          <div><b>Timeline:</b> {config.timelines.find((t) => t.id === form.timeline)?.label}</div>
        </div>
      </div>

      {result?.temperature && (
        <div className="text-[11px] text-muted-foreground">
          Lead score: <b>{result.score}</b> · Temperatura: <b>{result.temperature.toUpperCase()}</b>
        </div>
      )}
    </div>
  );
}
